import { NavigationItem } from "../types";

export const createMainNavigationData = (
  totalUnreadHandled: number = 0, // Badge count for handled chats
  totalUnreadUnhandled: number = 0, // Badge count for inbox (unhandled)
  t: (key: string) => string = (key) => key,
  logoutHandler?: () => void
): NavigationItem[] => {
  return [
    // V2 Menu Structure - Main navigation items
    {
      id: "dashboard",
      title: t("SideBar.MenuItem.Dashboard"),
      icon: "grid_view",
      path: "/home",
    },
    {
      id: "products",
      title: t("SideBar.MenuItem.Products"),
      icon: "storefront",
      path: "#",
      children: [
        {
          id: "product-listings",
          title: t("SideBar.MenuItem.ProductListings"),
          icon: "",
          path: "/product/listing",
        },
        {
          id: "merchant-products",
          title: t("SideBar.MenuItem.MerchantProducts"),
          icon: "",
          path: "/product/merchant",
        },
      ],
    },
    {
      id: "inventories",
      title: t("SideBar.MenuItem.Inventories"),
      icon: "inventory_2",
      path: "#",
      children: [
        {
          id: "import-stocks",
          title: t("SideBar.MenuItem.ImportStocks"),
          icon: "",
          path: "/product/import-stock",
        },
      ],
    },
    {
      id: "orders",
      title: t("SideBar.MenuItem.Orders"),
      icon: "receipt_long",
      path: "#",
      children: [
        {
          id: "export-orders",
          title: t("SideBar.MenuItem.ExportOrders"),
          icon: "",
          path: "/order/export",
        },
        {
          id: "all-orders",
          title: t("SideBar.MenuItem.AllOrders"),
          icon: "",
          path: "/order",
        },
        {
          id: "value-pack-orders",
          title: t("SideBar.MenuItem.ValuePackOrders"),
          icon: "",
          path: "/order/prePack",
        },
      ],
    },
    // {
    //   id: "incoming-chats",
    //   title: t("SideBar.MenuItem.IncomingChats"),
    //   icon: "move_to_inbox",
    //   path: "/ciq",
    //   badgeCount: totalUnreadUnhandled ?? 0,
    //   badgeColor: "danger",
    // },
    {
      id: "handled-chats",
      title: t("SideBar.MenuItem.HandledChats"),
      icon: "drafts",
      path: "/handled-customer",
      badgeCount: totalUnreadHandled ?? 0,
      badgeColor: "info", // Blue badge for handled chats (less urgent than red)
    },
    {
      id: "contacts",
      title: t("SideBar.MenuItem.Contacts"),
      icon: "contacts",
      path: "#",
      children: [
        {
          id: "customers",
          title: t("SideBar.MenuItem.Customer"),
          icon: "",
          path: "/contact",
        },
        {
          id: "user-networks",
          title: t("SideBar.MenuItem.FriendList"),
          icon: "",
          path: "/contact",
        },
      ],
    },
    {
      id: "marketing",
      title: t("SideBar.MenuItem.Marketing"),
      icon: "campaign",
      path: "#",
      children: [
        {
          id: "campaigns",
          title: t("SideBar.MenuItem.Campaigns"),
          icon: "",
          path: "/campaign",
        },
        {
          id: "templates",
          title: t("SideBar.MenuItem.Templates"),
          icon: "",
          path: "/template",
        },
      ],
    },
    {
      id: "settings",
      title: t("SideBar.MenuItem.Settings"),
      icon: "settings",
      path: "#",
    },
    // Bottom item
    {
      id: "logout",
      title: t("SideBar.MenuItem.Logout"),
      icon: "logout",
      path: "#",
      onClick: logoutHandler,
    },
  ];
};

export const createSettingsNavigationData = (
  t: (key: string) => string = (key) => key
): NavigationItem[] => {
  return [
    {
      id: "whatsapp-business",
      title: t("SideBar.MenuItem.WhatsappBusiness"),
      icon: "chat",
      path: "#",
      children: [
        {
          id: "add-whatsapp-business",
          title: t("SideBar.MenuItem.AddWhatsappBusinessAccount"),
          icon: "",
          path: "/setting/waba-account/create/sign-up",
        },
        {
          id: "whatsapp-business-list",
          title: t("SideBar.MenuItem.WhatsappBusinessAccountList"),
          icon: "",
          path: "/setting/waba-account",
        },
      ],
    },
    {
      id: "marketplace",
      title: t("SideBar.MenuItem.Marketplace"),
      icon: "store",
      path: "#",
      children: [
        {
          id: "channel-accounts",
          title: t("SideBar.MenuItem.ChannelAccounts"),
          icon: "",
          path: "/setting/marketplace",
        },
        {
          id: "shipping-configuration",
          title: t("SideBar.MenuItem.ShippingConfiguration"),
          icon: "",
          path: "/setting/marketplace",
        },
      ],
    },
    {
      id: "billing-subscription",
      title: t("SideBar.MenuItem.BillingSubscription"),
      icon: "credit_card",
      path: "/setting/billing",
    },
    {
      id: "api-integration",
      title: t("SideBar.MenuItem.ApiIntegration"),
      icon: "api",
      path: "/setting/api",
    },
  ];
};

// Backward compatibility function
export const createNavigationData = (
  totalUnreadHandled: number = 0,
  totalUnreadUnhandled: number = 0,
  t: (key: string) => string = (key) => key,
  logoutHandler?: () => void
): NavigationItem[] => {
  return createMainNavigationData(
    totalUnreadHandled,
    totalUnreadUnhandled,
    t,
    logoutHandler
  );
};

export const mapIconToMaterial = (originalIcon: string): string => {
  const iconMap: { [key: string]: string } = {
    "fas fa-home": "home",
    "fas fa-box": "inventory_2",
    "fas fa-clipboard": "assignment",
    "bi-app-indicator": "chat",
    "fas fa-users": "contacts",
    "bi bi-megaphone-fill": "campaign",
    "fas fa-cog": "settings",
    "fas fa-question-circle": "help",
    "fas fa-sign-out-alt": "logout",
  };

  return iconMap[originalIcon] || "circle";
};

export interface BottomNavigationItem {
  id: string;
  title: string;
  icon: string;
  path: string;
  badgeCount?: number;
}

export const createBottomNavigationData = (
  totalUnreadHandled: number = 0,
  totalUnreadUnhandled: number = 0,
  t: (key: string) => string = (key) => key
): BottomNavigationItem[] => {
  return [
    {
      id: "products",
      title: t("SideBar.MenuItem.Products"),
      icon: "storefront",
      path: "/product/listing",
    },
    {
      id: "orders",
      title: t("SideBar.MenuItem.Orders"),
      icon: "receipt_long",
      path: "/order",
    },
    // {
    //   id: "incoming-chats",
    //   title: t("SideBar.MenuItem.IncomingChats"),
    //   icon: "move_to_inbox",
    //   path: "/ciq",
    //   badgeCount: totalUnreadUnhandled,
    // },
    {
      id: "handled-chats",
      title: t("SideBar.MenuItem.HandledChats"),
      icon: "drafts",
      path: "/handled-customer",
      badgeCount: totalUnreadHandled,
    },
    {
      id: "settings",
      title: t("SideBar.MenuItem.Settings"),
      icon: "settings",
      path: "/setting",
    },
  ];
};
